import React, { useState, useEffect, useRef } from 'react'
import 'feather-icons'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import { ButtonLight, ButtonFaded } from '../ButtonStyled'
import { AutoRow, RowBetween } from '../Row'
import { isAddress } from '../../utils'
import { useSavedAccounts } from '../../contexts/LocalStorage'
import { AutoColumn } from '../Column'
import { TYPE } from '../../Theme'
import { Hover, StyledIcon } from '..'
import Panel from '../Panel'
import { Divider } from '..'
import { Flex } from 'rebass'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'

import { X } from 'react-feather'
import useToast from '../../hooks/useToast'

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  border-radius: 12px;
`

const Input = styled.input`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  white-space: nowrap;
  background: none;
  border: none;
  outline: none;
  padding: 12px 16px;
  border-radius: 12px;
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg1};
  font-size: 16px;
  margin-right: 1rem;
  border: 1px solid ${({ theme }) => theme.bg3};

  ::placeholder {
    color: ${({ theme }) => theme.text3};
    font-size: 14px;
  }

  @media screen and (max-width: 640px) {
    ::placeholder {
      font-size: 1rem;
    }
  }
`

const AccountLink = styled.span`
  display: flex;
  cursor: pointer;
  color: ${({ theme }) => theme.link};
  font-size: 14px;
  font-weight: 500;
`

const DashGrid = styled.div`
  display: grid;
  grid-gap: 1em;
  grid-template-columns: 1fr;
  grid-template-areas: 'account';
  padding: 0 4px;

  > * {
    justify-content: flex-end;
  }
`

function AccountSearch({ history, small }) {
  const { t } = useTranslation()
  const [accountValue, setAccountValue] = useState()
  const [savedAccounts, addAccount, removeAccount] = useSavedAccounts()
  const [showToast, toastMarkup] = useToast()
  const [isThrottled, setIsThrottled] = useState(false)
  const timeoutRef = useRef(null)

  async function resolveENSName(mnsName) {
    // testnet 'https://wannsee-rpc.mxc.com'
    const provider = new ethers.providers.JsonRpcProvider(
      process.env.REACT_APP_CHAIN_ID === 18686 ? 'https://rpc.mxc.com' : 'https://rpc.mxc.com'
    )
    // testnet '0x438b261bEb8D3C500153DD17588E6feC36535312'
    const address =
      process.env.REACT_APP_CHAIN_ID === 18686
        ? '0x5325640Cc17A06a409d4f4b6af02A0120528c67E'
        : '0x5325640Cc17A06a409d4f4b6af02A0120528c67E'

    const resolverContract = new ethers.Contract(
      address,
      [
        {
          inputs: [
            {
              internalType: 'bytes32',
              name: 'node',
              type: 'bytes32',
            },
          ],
          name: 'addr',
          outputs: [
            {
              internalType: 'address payable',
              name: '',
              type: 'address',
            },
          ],
          stateMutability: 'view',
          type: 'function',
        },
      ],
      provider
    )

    try {
      const addr = await resolverContract.addr(ethers.utils.namehash(mnsName)) // if mnsName is not exist, it will return 0x0000000000000000000000000000000000000000
      console.log(`The address of ${mnsName} is ${addr}`)
      return addr === '0x0000000000000000000000000000000000000000' ? undefined : addr
    } catch (error) {
      console.error(`Error resolving ENS name: ${error}`)
    }
  }

  function handleAccountSearch() {
    if (isThrottled) return
    setIsThrottled(true)
    if (isAddress(accountValue)) {
      history.push('/account/' + accountValue)
      if (!savedAccounts.includes(accountValue)) {
        addAccount(accountValue)
      }
    } else {
      resolveENSName(accountValue).then((addr) => {
        if (addr) {
          history.push('/account/' + addr, { mnsName: accountValue })
          if (!savedAccounts.includes(addr)) {
            addAccount(addr)
          }
        } else {
          showToast(t('No results'), 2000)
        }
        timeoutRef.current = setTimeout(() => {
          setIsThrottled(false)
        }, 2000)
      })
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <AutoColumn gap={'1rem'}>
      {!small && (
        <>
          <AutoRow>
            <Wrapper>
              <Input
                placeholder="0x..."
                onChange={(e) => {
                  setAccountValue(e.target.value)
                }}
              />
            </Wrapper>
            <ButtonLight onClick={handleAccountSearch}>{t('Load Account Details')}</ButtonLight>
          </AutoRow>
        </>
      )}

      <AutoColumn gap={'12px'}>
        {!small && (
          <Panel>
            <DashGrid center={true} style={{ height: 'fit-content', padding: '0 0 1rem 0' }}>
              <TYPE.main area="account">{t('Saved Accounts')}</TYPE.main>
            </DashGrid>
            <Divider />
            {savedAccounts?.length > 0 ? (
              savedAccounts.map((account) => {
                return (
                  <DashGrid key={account} center={true} style={{ height: 'fit-content', padding: '1rem 0 0 0' }}>
                    <Flex
                      area="account"
                      justifyContent="space-between"
                      onClick={() => history.push('/account/' + account)}
                    >
                      <AccountLink>{account?.slice(0, 42)}</AccountLink>
                      <Hover
                        onClick={(e) => {
                          e.stopPropagation()
                          removeAccount(account)
                        }}
                      >
                        <StyledIcon>
                          <X size={16} />
                        </StyledIcon>
                      </Hover>
                    </Flex>
                  </DashGrid>
                )
              })
            ) : (
              <TYPE.light style={{ marginTop: '1rem' }}>{t('No saved accounts')}</TYPE.light>
            )}
          </Panel>
        )}

        {small && (
          <>
            <TYPE.main>{'Accounts'}</TYPE.main>
            {savedAccounts?.length > 0 ? (
              savedAccounts.map((account) => {
                return (
                  <RowBetween key={account}>
                    <ButtonFaded onClick={() => history.push('/account/' + account)}>
                      {small ? (
                        <TYPE.header>{account?.slice(0, 6) + '...' + account?.slice(38, 42)}</TYPE.header>
                      ) : (
                        <AccountLink>{account?.slice(0, 42)}</AccountLink>
                      )}
                    </ButtonFaded>
                    <Hover onClick={() => removeAccount(account)}>
                      <StyledIcon>
                        <X size={16} />
                      </StyledIcon>
                    </Hover>
                  </RowBetween>
                )
              })
            ) : (
              <TYPE.light>{t('No pinned wallets')}</TYPE.light>
            )}
          </>
        )}
      </AutoColumn>
      {toastMarkup}
    </AutoColumn>
  )
}

export default withRouter(AccountSearch)
